import { bootstrap } from "fw";
import { ContainerInstance } from "fw";
import { setupStores, dispatch } from "fw-state";
import { parseJwt } from "helpers/parseJwt";

import "./app.css";

import { resetAllOnce } from "helpers/once";
import { App } from "./App";

import { registerComponents } from "bootstrap-components";

import vClickOutside from "v-click-outside";
import PortalVue from "portal-vue";
import DateRangePicker from "vue2-daterange-picker";
import Filters from "filters/numbers";
import TimeFilters from "filters/time";

import { Popover } from "components/popover";
import { Modal } from "components/modal";
import { ModalHeader } from "components/modal-header";
import { ModalWithHeader } from "components/modal-with-header";
import { Alert } from "components/alert";
import { DestroyAlert } from "components/destroy-alert";
import { Loader } from "./views/components/loader";

import { TextField } from "components/input/text-field";
import { DropdownField } from "components/input/dropdown-field";
import { TextareaField } from "components/input/textarea-field";
import { CheckboxField } from "components/input/checkbox-field";
import { FileField } from "components/input/file-field";
import { Toggle } from "components/input/toggle";

import { StartAction, LogoutAction } from "state/actions";
import { ActivityStore } from "state/activity";
import { CurrentUserStore } from "state/current-user";
import { AtsOrganizationStore } from "state/ats-organizations";
import { AtsOrganizationIntegrationsStore } from "state/ats-organization-integrations";
import { AtsOrganizationTemplateStore } from "state/ats-organization-templates";
import { AtsTemplateListStore } from "state/ats-organization-template-list";
import { CurrentAtsOrganizationSummaryStore } from "state/current-ats-organization-summary";
import { CurrentCasOrganizationStore } from "state/current-cas-organization";
import { SearchStore } from "state/search";
import { UserStore } from "state/users";
import { MonitorExportsStore } from "state/monitor-exports";
import { MonitorApplicationImportsStore } from "state/monitor-application-imports";
import { AppSyncStore } from "state/monitor-appsync-results";
import { ManagerConfig } from "config/manager-config";

import { Manager } from "network/manager";
import { UserRepository } from "network/user-repository";
import { User } from "models/user";
import { GridStore } from "./state/components/grid";
import { AtsOrganizationGridStore } from "./state/ats-organization-list";
import { AlarmsStore } from "./state/alarms";
import { ConfigStore, SetConfigAction } from "state/config";
import { ChartLegend } from "components/chart-legend";
import { AppMetricsStore } from "state/app-metrics";
import { BackupsStore } from "state/backups";
import { CommonAppIntegrationsStore } from "state/common-app-integrations";
import { DashboardMetricsStore } from "state/dashboard-metrics";
import { ElasticsearchStore } from "state/elasticsearch";
import { FeatureFlagsDetailsStore } from "state/feature-flags-details";
import { FeatureFlagsImplicitsStore } from "state/feature-flags-implicits";
import { AppSyncDetailsStore } from "state/monitor-appsync-results-details";
import { MonitorAutomationRulesStore } from "state/monitor-automation-rules";
import { MonitorCommonAppTransfersStore } from "state/monitor-common-app-transfers";
import { MonitorFileSourcePullHistoriesStore } from "state/monitor-file-source-pull-histories";
import { MonitorInterviewPortalExportHistoriesStore } from "state/monitor-interview-portal-export-histories";
import { MonitorWorkflowsStore } from "state/monitor-workflows";
import { MonitorEmailStore } from "state/monitor-email";
import { NotificationsStore } from "state/notifications";
import { TransferPlannerDetectionSummaryStore } from "state/transfer-planner-detection-summary";

bootstrap(async (c) => {
    c.useVuePlugin(vClickOutside);
    c.useVuePlugin(PortalVue);
    c.useVuePlugin((v) => v.component("DateRangePicker", DateRangePicker));
    c.useVuePlugin(Filters);
    c.useVuePlugin(TimeFilters);

    const managerConfig = await c.withConfig(ManagerConfig, process.env.MANAGER_CONFIG_FILE || "manager-config.json");
    if (localStorage?.["apiUrl"]) {
        managerConfig.apiUrl = localStorage["apiUrl"];
    }

    c.registerComponents(
        Popover,
        ModalHeader,
        Modal,
        ModalWithHeader,
        Alert,
        DestroyAlert,
        Loader,

        TextField,
        CheckboxField,
        DropdownField,
        TextareaField,
        FileField,
        Toggle,
        ChartLegend
    );

    registerComponents(c);

    setupStores(
        ContainerInstance,
        CurrentUserStore,
        GridStore,
        ActivityStore,
        AlarmsStore,
        AppMetricsStore,
        AtsOrganizationStore,
        AtsOrganizationGridStore,
        AtsOrganizationIntegrationsStore,
        AtsTemplateListStore,
        AtsOrganizationTemplateStore,
        BackupsStore,
        CommonAppIntegrationsStore,
        AppSyncStore,
        ConfigStore,
        CurrentAtsOrganizationSummaryStore,
        CurrentCasOrganizationStore,
        DashboardMetricsStore,
        ElasticsearchStore,
        FeatureFlagsDetailsStore,
        FeatureFlagsImplicitsStore,
        MonitorApplicationImportsStore,
        AppSyncDetailsStore,
        MonitorAutomationRulesStore,
        MonitorCommonAppTransfersStore,
        MonitorFileSourcePullHistoriesStore,
        MonitorInterviewPortalExportHistoriesStore,
        MonitorWorkflowsStore,
        MonitorEmailStore,
        NotificationsStore,
        SearchStore,
        TransferPlannerDetectionSummaryStore,
        UserStore,
        MonitorExportsStore
    );

    await dispatch(new SetConfigAction(managerConfig));

    const manager = ContainerInstance.get(Manager);
    let currentUser;

    if (manager.hasToken) {
        try {
            const userRepo = ContainerInstance.get(UserRepository);
            currentUser = await userRepo.me();
        } catch (err) {
            console.log(err);
        }

        // reset the lazy load tracking for all states
        resetAllOnce();
        if (currentUser) {
            await dispatch(new StartAction(currentUser, manager.token));
        } else {
            try {
                const jwt = parseJwt(manager.token);
                if (jwt["manager-type"] == "System") {
                    currentUser = new User();
                    currentUser["FirstName"] = "System";
                    currentUser["LastName"] = "User";
                    await dispatch(new StartAction(currentUser, manager.token));
                } else {
                    await dispatch(new LogoutAction());
                    manager.deleteToken();
                    throw "Manager type is not valid";
                }
            } catch (err) {
                console.log(err);
                await dispatch(new LogoutAction());
                manager.deleteToken();
            }
        }
    }

    stopFormSubmit();

    // parm handling
    const hash = window.location.hash;

    // lifespan
    if (hash.startsWith("#/lifespan="))
        if (hash.startsWith("#/lifespan=")) {
            manager.setTokenLifespan(parseInt(hash.substr(11), 10));
            window.location.hash = "#/";
        }

    c.startWith(App);
});

function stopFormSubmit() {
    document.addEventListener("submit", (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
    });
}
